import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowClockwise, FileXls, Trash, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError } from "../lib/api";
import { Button } from "./ui/button";

const dateLabel = (value) => (value ? new Date(value).toLocaleString("pt-BR") : "—");

export default function MedcofImportBlock() {
  const [options, setOptions] = useState({ schools: [], can_import_school_ids: [] });
  const [imports, setImports] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [files, setFiles] = useState([]);
  const [importing, setImporting] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [lastImport, setLastImport] = useState(null);

  const manageableSchools = useMemo(() => {
    const allowed = new Set(options.can_import_school_ids || []);
    return (options.schools || []).filter((school) => allowed.has(school.id));
  }, [options]);

  const load = useCallback(async () => {
    const [optionsResponse, importsResponse] = await Promise.all([
      api.get("/analytics/medcof/options"),
      api.get("/analytics/medcof/imports"),
    ]);
    const next = optionsResponse.data || {};
    setOptions(next);
    setImports(importsResponse.data?.imports || []);
    const allowed = new Set(next.can_import_school_ids || []);
    const selected = allowed.has(next.selected_school_id)
      ? next.selected_school_id
      : (next.schools || []).find((school) => allowed.has(school.id))?.id || "";
    setSchoolId((current) => (allowed.has(current) ? current : selected));
  }, []);

  useEffect(() => {
    load().catch((error) => toast.error("Falha ao preparar a importação MEDCOF: " + fmtError(error)));
  }, [load]);

  const importWorkbooks = async () => {
    if (!files.length || !schoolId) return;
    setImporting(true);
    const totals = { files: files.length, rows: 0, matched: 0, unmatched: 0 };
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("school_id", schoolId);
        form.append("file", file);
        // Cada arquivo mantém sua própria unidade de auditoria e reversão.
        // eslint-disable-next-line no-await-in-loop
        const response = await api.post("/analytics/medcof/import", form);
        const counts = response.data?.counts || {};
        totals.rows += counts.rows || 0;
        totals.matched += (counts.matched_cpf || 0) + (counts.matched_email || 0) + (counts.matched_name || 0);
        totals.unmatched += counts.unmatched || 0;
      }
      setLastImport(totals);
      setFiles([]);
      toast.success(`${totals.files} arquivo(s) importado(s): ${totals.matched} aluno(s) vinculado(s).`);
      await load();
    } catch (error) {
      toast.error("Falha ao importar MEDCOF: " + fmtError(error));
    } finally {
      setImporting(false);
    }
  };

  const deleteImport = async (item) => {
    if (!window.confirm(`Apagar a importação inteira “${item.filename}”? Os dados anteriores serão restaurados quando houver histórico.`)) return;
    setDeletingId(item.id);
    try {
      const response = await api.delete(`/analytics/medcof/imports/${item.id}`);
      const rollback = response.data?.rollback || {};
      toast.success(`Importação apagada: ${rollback.deleted_inserted_rows || 0} linha(s) removida(s) e ${rollback.restored_previous_rows || 0} restaurada(s).`);
      await load();
    } catch (error) {
      toast.error("Falha ao apagar a importação MEDCOF: " + fmtError(error));
    } finally {
      setDeletingId("");
    }
  };

  if (!manageableSchools.length) return null;

  return (
    <section className="border border-border bg-card mb-6" data-testid="admin-medcof-import">
      <div className="border-b border-border px-6 py-4 flex items-center justify-between gap-4">
        <div>
          <div className="label-eyebrow">integrações acadêmicas</div>
          <h2 className="font-display text-2xl font-bold tracking-tight mt-1">Importar e administrar histórico MEDCOF</h2>
        </div>
        <FileXls size={26} weight="duotone" className="text-primary" />
      </div>
      <div className="p-6">
        <p className="text-sm text-muted-foreground max-w-3xl">Envie uma ou várias planilhas no padrão MEDCOF. O vínculo prioriza CPF; quando ele não existir na planilha, usa e-mail e, por último, nome exato. Cada arquivo pode ser revertido integralmente abaixo.</p>
        <div className="grid lg:grid-cols-[minmax(220px,0.7fr)_minmax(280px,1.3fr)_auto] gap-3 items-end mt-5">
          <label><span className="label-eyebrow block mb-2">escola de destino</span><select value={schoolId} onChange={(event) => setSchoolId(event.target.value)} className="w-full border border-border bg-background px-3 py-2.5 text-sm">{manageableSchools.map((school) => <option key={school.id} value={school.id}>{school.name}</option>)}</select></label>
          <label><span className="label-eyebrow block mb-2">planilhas XLSX</span><input key={files.length ? "selected" : "empty"} aria-label="Planilhas MEDCOF" type="file" accept=".xlsx" multiple onChange={(event) => setFiles(Array.from(event.target.files || []))} className="block w-full border border-border bg-background px-3 py-2 text-sm" /></label>
          <Button onClick={importWorkbooks} disabled={!files.length || !schoolId || importing} className="rounded-none h-[42px]"><UploadSimple size={17} className="mr-2" />{importing ? "Importando…" : `Importar${files.length > 1 ? ` ${files.length} arquivos` : ""}`}</Button>
        </div>
        {lastImport && <div className="mt-4 border border-primary/30 bg-primary/5 p-4 text-sm font-mono-data">Última importação: {lastImport.rows} linha(s) · {lastImport.matched} vinculada(s) · {lastImport.unmatched} não encontrada(s).</div>}

        <div className="mt-7 flex items-end justify-between gap-4">
          <div><div className="label-eyebrow">auditoria e reversão</div><h3 className="font-display text-xl font-black mt-1">Importações realizadas</h3></div>
          <Button variant="outline" className="rounded-none" onClick={load}><ArrowClockwise size={16} className="mr-2" />Atualizar</Button>
        </div>
        <div className="border border-border mt-3 divide-y divide-border" data-testid="admin-medcof-import-history">
          {imports.map((item) => {
            const counts = item.counts || {};
            const matched = (counts.matched_cpf || 0) + (counts.matched_email || 0) + (counts.matched_name || 0);
            const deleted = item.status === "deleted";
            return (
              <div key={item.id} className={`p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4 ${deleted ? "opacity-60 bg-muted/20" : ""}`}>
                <div className="min-w-0">
                  <div className="font-mono-data font-bold truncate" title={item.filename}>{item.filename}</div>
                  <div className="text-xs text-muted-foreground mt-1">{item.school_name || "Escola"} · {dateLabel(item.imported_at)} · {counts.rows || 0} linha(s) · {matched} vinculada(s)</div>
                  <div className="text-xs mt-1">{deleted ? `Apagada · ${item.rollback?.deleted_inserted_rows || 0} removida(s) · ${item.rollback?.restored_previous_rows || 0} restaurada(s)` : `${item.periods?.length || 0} período(s) · reversão disponível`}</div>
                </div>
                {!deleted && (
                  <Button variant="outline" className="rounded-none border-destructive text-destructive hover:text-destructive shrink-0" onClick={() => deleteImport(item)} disabled={deletingId === item.id}>
                    <Trash size={16} className="mr-2" />{deletingId === item.id ? "Apagando…" : "Apagar importação"}
                  </Button>
                )}
              </div>
            );
          })}
          {!imports.length && <div className="p-8 text-center text-sm text-muted-foreground">Nenhuma importação MEDCOF registrada.</div>}
        </div>
      </div>
    </section>
  );
}
